import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, Box,
  BottomNavigation, BottomNavigationAction, Paper, useMediaQuery, useTheme, Button,
} from '@mui/material';
import {
  Coffee, CalendarMonth, ConfirmationNumber, Person, Star, School, Loyalty, StoreMallDirectory,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import UserMenu from '../shared/UserMenu';
import LanguageSelector from '../shared/LanguageSelector';
import ChatWidget from '../shared/ChatWidget';
import NotificationsBell from '../notifications/NotificationsBell';
import pushService from '../../services/pushService';
import { colors, designTokens } from '../../theme/theme';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export default function UserLayout() {
  const navigate = useNavigate();
  const pushPrompted = useRef(false);
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isAuthenticated, user } = useAuth();
  const { t } = useTranslation();

  // Auto-subscribe to push after login (once per session)
  useEffect(() => {
    if (isAuthenticated && !pushPrompted.current && pushService.isSupported()) {
      pushPrompted.current = true;
      // Small delay so the page loads first
      const timer = setTimeout(() => {
        if (pushService.getPermissionState() === 'default') {
          pushService.subscribe().catch(() => { /* no-op */ });
        } else if (pushService.getPermissionState() === 'granted') {
          pushService.subscribe().catch(() => { /* no-op */ });
        }
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated]);

  const isPremiumOrPartner = user?.user_tier === 'premium' || user?.role === 'partner';

  const navItems = [
    { label: t('nav.home'), icon: <Coffee />, path: '/' },
    { label: t('nav.events'), icon: <CalendarMonth />, path: '/events' },
    { label: t('nav.cafes'), icon: <StoreMallDirectory />, path: '/venues' },
    { label: t('nav.learn'), icon: <School />, path: '/knowledge' },
    { label: t('nav.vouchers'), icon: <ConfirmationNumber />, path: '/vouchers' },
    { label: t('nav.loyalty'), icon: <Loyalty />, path: '/loyalty' },
    ...(isPremiumOrPartner ? [{ label: t('nav.dashboard'), icon: <Star />, path: '/dashboard' }] : []),
    { label: t('nav.profile'), icon: <Person />, path: '/profile' },
  ];

  const activeIdx = navItems.findIndex(n => location.pathname === n.path || location.pathname.startsWith(n.path + '/'));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* ── Top App Bar — Frosted glass effect ── */}
      <AppBar position="sticky" elevation={0}>
        <Toolbar sx={{ px: { xs: 2, md: 4 }, height: { xs: 60, md: 68 } }}>
          {/* Logo */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              flexGrow: 1,
              '&:hover': { opacity: 0.8 },
              transition: designTokens.transition.fast,
            }}
            onClick={() => navigate('/')}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: colors.primary.gradient,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '1.1rem',
              }}
            >
              <Coffee sx={{ fontSize: 20 }} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#FFFFFF',
                letterSpacing: '-0.02em',
                fontSize: '1.15rem',
              }}
            >
              CoffeeMates
            </Typography>
          </Box>

          {/* Desktop nav — a button row, not a list, so it can't use
              <NavListItem>; it mirrors NavListItem's role='user' visual
              contract (accent, ~12% selected opacity, 4% hover wash).
              Keep in visual lockstep with NavListItem. */}
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 0.5, mr: 2 }}>
              {navItems.slice(0, 4).map((item) => {
                const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                return (
                  <Button
                    key={item.label}
                    size="small"
                    onClick={() => navigate(item.path)}
                    aria-current={isActive ? 'page' : undefined}
                    sx={{
                      color: isActive ? '#FFFFFF' : colors.neutral[500],
                      fontWeight: isActive ? 700 : 500,
                      fontSize: '0.9rem',
                      borderRadius: 10,
                      minHeight: 36,
                      px: 2,
                      py: 0.8,
                      // Token-based 12% coral wash — mirrors NavListItem
                      // user-role active state (`${colors.primary.main}1F`).
                      bgcolor: isActive ? `${colors.primary.main}1F` : 'transparent',
                      '&:hover': {
                        bgcolor: isActive ? `${colors.primary.main}29` : 'rgba(255,255,255,0.04)',
                        color: isActive ? '#FFFFFF' : colors.neutral[700],
                      },
                      transition: designTokens.transition.fast,
                    }}
                  >
                    {item.label}
                  </Button>
                );
              })}
            </Box>
          )}

          {isAuthenticated ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <NotificationsBell />
              <UserMenu />
              {!isMobile && <LanguageSelector />}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              {!isMobile && <LanguageSelector />}
              <Button
                size="small"
                onClick={() => navigate('/login')}
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  px: 2,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.06)', color: '#FFFFFF' },
                }}
              >
                {t('auth.login')}
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => navigate('/login?mode=register')}
                sx={{
                  fontWeight: 600,
                  borderRadius: 12,
                  px: 2.5,
                  py: 0.8,
                  fontSize: '0.9rem',
                  background: colors.primary.gradient,
                  '&:hover': { boxShadow: designTokens.shadow.glow },
                }}
              >
                {t('auth.register')}
              </Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* ── Main Content ── */}
      <Box sx={{ flex: 1, pb: isMobile ? 10 : 2 }}>
        <Outlet />
      </Box>

      {/* ── Support Chat Widget ── */}
      <ChatWidget />

      {/* ── Bottom Navigation (Mobile) — Frosted glass ── */}
      {isMobile && isAuthenticated && (
        <Paper
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1100,
            borderTop: '1px solid rgba(255,255,255,0.06)',
            borderRadius: 0,
          }}
          elevation={0}
        >
          <BottomNavigation
            value={activeIdx >= 0 ? activeIdx : 0}
            onChange={(_, newValue) => {
              // eslint-disable-next-line security/detect-object-injection -- newValue is MUI BottomNavigation index, bounded by navItems.length
              const item = navItems[newValue];
              if (item) navigate(item.path);
            }}
            showLabels
            sx={{
              height: 68,
              pb: 'env(safe-area-inset-bottom)',
              '& .Mui-selected': {
                color: colors.primary.main,
                '& .MuiBottomNavigationAction-label': {
                  fontSize: '0.7rem',
                  fontWeight: 700,
                },
              },
              '& .MuiBottomNavigationAction-root': {
                minWidth: 'auto',
                color: colors.neutral[400],
                transition: designTokens.transition.fast,
              },
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.65rem',
                marginTop: '2px',
              },
            }}
          >
            {navItems.map((item) => (
              <BottomNavigationAction key={item.label} label={item.label} icon={item.icon} />
            ))}
          </BottomNavigation>
        </Paper>
      )}
    </Box>
  );
}
